import { ModelElementType } from "@/components/entities";
import { create } from "zustand";
import {
  type DoorOutcome,
  fetchModels,
  handleDeleteModel,
  handleHardDeleteModel,
  restoreModelInto,
  setModelStarred,
} from "@/components/main/utils/models/ModelServices";
import { catalogRowToModelElement } from "@/components/main/utils/models/types";
import { fetchCatalogModels } from "@/lib/api/catalog";
import { useTrashStore } from "./trashStore";
import { fetchWithInit, FetchListOptions } from "./fetchWithInit";
import { useOrgContextStore } from "./orgContextStore";
import {
  PendingLifecycleMap,
  PendingPatchMap,
  reconcile,
  rollbackLifecycle,
  rollbackPatch,
  stagePatch,
} from "./pendingModelMutations";

interface MainState {
  selectedModel: ModelElementType | null;
  setSelectedModel: (model: ModelElementType | null) => void;
  availableModels: ModelElementType[];
  /** The org `availableModels` was fetched for (`null` before any fetch). The
   *  list is the active org's only while this matches `activeOrgId`. */
  availableModelsOrgId: string | null;
  /** Local mutations no models response has echoed yet — see
   *  `pendingModelMutations`. Retired on echo, or after the grace window. */
  pendingLifecycle: PendingLifecycleMap;
  pendingPatches: PendingPatchMap;
  setAvailableModels: (models: ModelElementType[]) => void;
  removeModelFromAvailable: (model_id: string) => void;
  /**
   * Optimistically apply `fields` to a model and stage them against stale
   * polls. Returns a rollback that restores exactly these fields — call it
   * when the server request fails.
   */
  patchModel: (
    modelId: string,
    fields: Partial<ModelElementType>,
  ) => () => void;
  isLoading: boolean;
  error: string | null;
  fetchModelsList: (options?: FetchListOptions) => Promise<ModelElementType[]>;
  /** Refetch guaranteed to postdate the call — for after a mutation. */
  invalidateModels: () => Promise<ModelElementType[]>;
  /** Trashed catalog models. They belong to no org, so the org Trash never
   *  lists them; `null` until the first read answers. */
  catalogTrash: ModelElementType[] | null;
  catalogTrashError: boolean;
  /** Trash, restore and delete forever on a catalog model — the publish role. */
  canDeleteCatalogModels: boolean;
  fetchCatalogTrash: () => Promise<void>;
  /** Moves the model to the Trash; the row leaves the live list the moment
   *  the door agrees. `tableIds` are the tables scheduled with it. */
  trashModel: (model: ModelElementType, tableIds: string[]) => Promise<boolean>;
  /** Every restore goes through here: it answers the delete's tombstone. */
  restoreModel: (modelId: string) => Promise<DoorOutcome>;
  purgeModel: (modelId: string) => Promise<boolean>;
  addModel: (model: ModelElementType) => void;
  updateModel: (model: ModelElementType) => void;
  /**
   * Optimistically flip `is_starred` for a model and persist via the API.
   * Rolls back the local state if the request fails. Resolves to the final
   * `is_starred` value (which equals the requested target on success, or the
   * original value on failure).
   */
  toggleStar: (modelId: string, nextStarred: boolean) => Promise<boolean>;
  setError: (message: string | null) => void;
}

// Bumped by every catalog-trash read AND every local move, so a response
// that started before either can't overwrite what the move already shows.
let catalogTrashSeq = 0;

const withoutModel = (
  models: ModelElementType[],
  modelId: string,
): ModelElementType[] => models.filter((m) => m.model_id !== modelId);

/** The live list without the model, plus the tombstone that keeps a stale
 *  poll from resurrecting it. An unconfirmed add just clears. */
const removeLive = (
  state: Pick<MainState, "availableModels" | "pendingLifecycle">,
  modelId: string,
): Pick<MainState, "availableModels" | "pendingLifecycle"> => {
  const row = state.availableModels.find((m) => m.model_id === modelId);
  return {
    availableModels: withoutModel(state.availableModels, modelId),
    pendingLifecycle:
      state.pendingLifecycle[modelId]?.kind === "add" || !row
        ? rollbackLifecycle(state.pendingLifecycle, modelId)
        : {
            ...state.pendingLifecycle,
            [modelId]: { kind: "remove", at: Date.now(), row },
          },
  };
};

export const useMainStateStore = create<MainState>((set, get) => ({
  selectedModel: null,
  setSelectedModel: (model) => {
    set({ selectedModel: model });
  },
  availableModels: [],
  availableModelsOrgId: null,
  pendingLifecycle: {},
  pendingPatches: {},
  setAvailableModels: (models) => {
    const {
      models: merged,
      lifecycle,
      patches,
    } = reconcile(models, get().pendingLifecycle, get().pendingPatches);

    const selModelId = get().selectedModel?.model_id;
    const refreshed = selModelId
      ? merged.find((m) => m.model_id === selModelId)
      : undefined;
    set({
      availableModels: merged,
      pendingLifecycle: lifecycle,
      pendingPatches: patches,
      selectedModel: refreshed
        ? refreshed
        : selModelId && !merged.some((e) => e.model_id === selModelId)
          ? null
          : get().selectedModel,
    });
  },
  removeModelFromAvailable: (model_id: string) => {
    set((state) => removeLive(state, model_id));
  },
  patchModel: (modelId, fields) => {
    const current = get().availableModels.find((m) => m.model_id === modelId);
    if (!current) return () => {};
    set((state) => ({
      pendingPatches: stagePatch(
        state.pendingPatches,
        modelId,
        fields,
        current,
      ),
    }));
    get().updateModel({ ...current, ...fields });

    const keys = Object.keys(fields) as (keyof ModelElementType)[];
    return () => {
      const { patches, restore } = rollbackPatch(
        get().pendingPatches,
        modelId,
        keys,
      );
      set({ pendingPatches: patches });
      const row = get().availableModels.find((m) => m.model_id === modelId);
      if (row) get().updateModel({ ...row, ...restore });
    };
  },
  isLoading: false,
  error: null,
  fetchModelsList: async (options) => {
    // `fetchModels` narrows by the active org at call time; a response that
    // lost a race with an org switch is dropped.
    const forOrg = useOrgContextStore.getState().activeOrgId;
    return fetchWithInit(set, options, {
      initFlag: "modelsInitialized",
      dataKey: "availableModels",
      cachedData: get().availableModels,
      fetchFn: fetchModels,
      setData: (models) => {
        if (useOrgContextStore.getState().activeOrgId !== forOrg) return;
        get().setAvailableModels(models);
        set({ availableModelsOrgId: forOrg });
      },
      errorMessage: "Failed to fetch models",
    });
  },
  invalidateModels: () => get().fetchModelsList({ refresh: true, force: true }),
  catalogTrash: null,
  catalogTrashError: false,
  canDeleteCatalogModels: false,
  fetchCatalogTrash: async () => {
    const seq = ++catalogTrashSeq;
    try {
      const { can_delete, models } = await fetchCatalogModels();
      if (seq !== catalogTrashSeq) return;
      set({
        catalogTrash: models
          .filter((m) => m.scheduled_deletion_date != null)
          .map(catalogRowToModelElement),
        canDeleteCatalogModels: can_delete,
        catalogTrashError: false,
      });
    } catch {
      if (seq === catalogTrashSeq) set({ catalogTrashError: true });
    }
  },
  trashModel: async (model, tableIds) => {
    const ok = await handleDeleteModel(model.model_id, model.name, tableIds);
    if (!ok) return false;
    catalogTrashSeq++;
    set((state) => ({
      ...removeLive(state, model.model_id),
      selectedModel:
        state.selectedModel?.model_id === model.model_id
          ? null
          : state.selectedModel,
      // The purge date is the server's; the row shows without one until the
      // read below brings it.
      catalogTrash:
        model.is_catalog && state.catalogTrash
          ? [
              ...withoutModel(state.catalogTrash, model.model_id),
              { ...model, scheduled_deletion_date: undefined },
            ]
          : state.catalogTrash,
    }));
    if (model.is_catalog) void get().fetchCatalogTrash();
    else
      void useTrashStore
        .getState()
        .refreshTrashModels()
        .catch(() => {});
    return true;
  },
  restoreModel: async (modelId) => {
    const outcome = await restoreModelInto(modelId);
    if (!outcome.ok) return outcome;
    const trashed = get().catalogTrash?.find((m) => m.model_id === modelId);
    const restored = trashed && {
      ...trashed,
      scheduled_deletion_date: undefined,
    };
    catalogTrashSeq++;
    // The delete left a tombstone that only a list WITHOUT the model retires,
    // so it is cleared here or the restore stays hidden until it lapses.
    set((state) => ({
      pendingLifecycle: restored
        ? {
            ...state.pendingLifecycle,
            [modelId]: { kind: "add", at: Date.now(), row: restored },
          }
        : rollbackLifecycle(state.pendingLifecycle, modelId),
      availableModels: restored
        ? [...withoutModel(state.availableModels, modelId), restored]
        : state.availableModels,
      catalogTrash: state.catalogTrash
        ? withoutModel(state.catalogTrash, modelId)
        : null,
    }));
    useTrashStore.getState().removeTrashModel(modelId);
    await get().invalidateModels();
    return outcome;
  },
  purgeModel: async (modelId) => {
    const ok = await handleHardDeleteModel(modelId);
    if (!ok) return false;
    catalogTrashSeq++;
    set((state) => ({
      catalogTrash: state.catalogTrash
        ? withoutModel(state.catalogTrash, modelId)
        : null,
    }));
    useTrashStore.getState().removeTrashModel(modelId);
    return true;
  },
  updateModel: (updatedModel) => {
    set((state) => {
      const existingIndex = state.availableModels.findIndex(
        (m) => m.model_id === updatedModel.model_id,
      );
      const pending = state.pendingLifecycle[updatedModel.model_id];
      return {
        availableModels:
          existingIndex >= 0
            ? state.availableModels.map((m) =>
                m.model_id === updatedModel.model_id ? updatedModel : m,
              )
            : [...state.availableModels, updatedModel],
        // A pending add re-inserts its staged row on reconcile — keep that
        // snapshot current so edits made meanwhile aren't re-inserted stale.
        pendingLifecycle:
          pending?.kind === "add"
            ? {
                ...state.pendingLifecycle,
                [updatedModel.model_id]: { ...pending, row: updatedModel },
              }
            : state.pendingLifecycle,
        selectedModel:
          state.selectedModel?.model_id === updatedModel.model_id
            ? updatedModel
            : state.selectedModel,
      };
    });
  },
  addModel: (model) => {
    const isNew = !get().availableModels.some(
      (m) => m.model_id === model.model_id,
    );
    if (isNew)
      set((state) => ({
        pendingLifecycle: {
          ...state.pendingLifecycle,
          [model.model_id]: { kind: "add", at: Date.now(), row: model },
        },
      }));
    get().updateModel(model);
  },
  toggleStar: async (modelId, nextStarred) => {
    const current = get().availableModels.find((m) => m.model_id === modelId);
    if (!current) return nextStarred;
    const previous = current.is_starred ?? false;
    if (previous === nextStarred) return nextStarred;

    const rollback = get().patchModel(modelId, { is_starred: nextStarred });

    const ok = await setModelStarred(modelId, nextStarred);
    if (!ok) {
      rollback();
      return previous;
    }
    return nextStarred;
  },
  setError: (message) => set({ error: message }),
}));
